import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useAIDiscoveryStore } from '../store/aiDiscoveryStore';
import { useEventStore } from '../store/eventStore';
import { useTodoStore } from '../store/todoStore';
import { useFamilyStore } from '../store/familyStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import { CoreNavRail } from '../components/CoreNavRail';
import NavBar from '../components/NavBar';
import { Icon } from '../utils/icons';
import { AIDiscovery, DiscoveryCandidate } from '../types/aiDiscovery';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToastStore } from '../store/toastStore';
import { formatDateTime, formatTimeRange, formatDate } from '../utils/time';
import { decodeJWT } from '../utils/jwt';

export default function Discoveries() {
  const navigate = useNavigate();
  const { isAuthenticated, idToken } = useAuthStore();
  const currentUserId = idToken ? decodeJWT(idToken)?.sub : null;
  const { family, fetchFamily } = useFamilyStore();
  const { 
    discoveries, 
    isLoading, 
    isUploading, 
    isProcessing, 
    error,
    getUploadUrl, 
    uploadFile, 
    processDiscovery, 
    fetchDiscoveries, 
    getDiscoveryImageUrl,
    confirmDiscovery, 
    dismissDiscovery,
    dismissCandidate,
    clearError 
  } = useAIDiscoveryStore();

  const [imageUrlCache, setImageUrlCache] = useState<Record<string, string>>({});
  const [fullImageUrl, setFullImageUrl] = useState<string | null>(null);
  const [dismissDiscoveryId, setDismissDiscoveryId] = useState<string | null>(null);
  const { createEvent } = useEventStore();
  const { createTodo } = useTodoStore();
  const { isDarkMode } = useThemeStore();
  const { showToast } = useToastStore();
  const tokens = getDesignTokens(isDarkMode);

  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const lastRecordingStartRef = useRef<number>(0);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchFamily()
        .then(() => {
          const currentFamily = useFamilyStore.getState().family;
          if (currentFamily === null) {
            navigate('/family', { replace: true });
          }
        })
        .catch((error) => {
          console.error('Failed to fetch family:', error);
          showToast(error instanceof Error ? error.message : 'Failed to load family', 'error');
        });
    }
  }, [isAuthenticated, fetchFamily, showToast, navigate]);

  useEffect(() => {
    // Fetch both processing and pending discoveries
    fetchDiscoveries('processing', false).catch((error) => {
      console.error('Error fetching processing discoveries:', error);
    });
    fetchDiscoveries('pending', false).catch((error) => {
      console.error('Error fetching pending discoveries:', error);
      showToast(error instanceof Error ? error.message : 'Failed to load discoveries', 'error');
    });
  }, [fetchDiscoveries, showToast]);

  useEffect(() => {
    if (error) {
      showToast(error, 'error');
      clearError();
    }
  }, [error, showToast, clearError]);

  // Fetch image URLs for image discoveries when they appear
  useEffect(() => {
    const imageDiscoveries = discoveries.filter(
      d => d.sourceType === 'image' && !imageUrlCache[d.discoveryId]
    );
    imageDiscoveries.forEach(async (discovery) => {
      try {
        const url = await getDiscoveryImageUrl(discovery.discoveryId);
        setImageUrlCache(prev => ({ ...prev, [discovery.discoveryId]: url }));
      } catch (e) {
        console.error('Failed to load discovery image:', e);
      }
    });
  }, [discoveries, getDiscoveryImageUrl, imageUrlCache]);

  useEffect(() => {
    if (!fullImageUrl) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullImageUrl(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [fullImageUrl]);

  const startRecording = async () => {
    // Validate API availability - must run before any await to preserve user gesture context
    // Check secure context first - mediaDevices is undefined on HTTP (most common cause on mobile)
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      showToast('Microphone requires HTTPS. Please open the app at https://app.runow.ai', 'error');
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      showToast('Microphone not available. Try Chrome and ensure you\'re on https://app.runow.ai', 'error');
      return;
    }

    try {
      // Call getUserMedia synchronously in user gesture - don't await anything before this
      const streamPromise = navigator.mediaDevices.getUserMedia({ audio: true });
      const stream = await streamPromise;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks: BlobPart[] = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setRecording(true);
      setRecordingTime(0);

      // 25 second limit
      const interval = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 25) {
            stopRecording();
            return 25;
          }
          return prev + 1;
        });
      }, 1000);
      recordingIntervalRef.current = interval;
    } catch (error) {
      console.error('Error starting recording:', error);
      showToast('Failed to start recording. Please check microphone permissions.', 'error');
    }
  };

  // Wrapper to prevent double-firing when both pointerup and click fire (e.g. on touch devices)
  const handleStartRecordingClick = (_e: React.MouseEvent | React.PointerEvent) => {
    const now = Date.now();
    if (now - lastRecordingStartRef.current < 500) return;
    lastRecordingStartRef.current = now;
    startRecording();
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const handleAudioUpload = async () => {
    if (!audioBlob) return;

    try {
      const fileName = `recording-${Date.now()}.webm`;
      const { uploadUrl, fileKey } = await getUploadUrl(fileName, 'audio/webm', audioBlob.size);
      await uploadFile(new File([audioBlob], fileName, { type: 'audio/webm' }), uploadUrl);
      
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const result = await processDiscovery(fileKey, 'audio', timeZone);
      
      showToast('Processing started. Results will appear shortly.', 'success');
      
      // Reset audio
      setAudioBlob(null);
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      
      // Start polling for updates
      startPolling(result.discoveryId);
    } catch (error: any) {
      console.error('Error uploading audio:', error);
      showToast(error.message || 'Failed to process audio', 'error');
    }
  };

  const handleFileUpload = async (file: File, sourceType: 'audio' | 'image') => {
    try {
      const { uploadUrl, fileKey } = await getUploadUrl(file.name, file.type, file.size);
      await uploadFile(file, uploadUrl);
      
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const result = await processDiscovery(fileKey, sourceType, timeZone);
      
      showToast('Processing started. Results will appear shortly.', 'success');
      
      // Start polling for updates
      startPolling(result.discoveryId);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      showToast(error.message || 'Failed to process file', 'error');
    }
  };

  const handleConfirm = async (discovery: AIDiscovery, candidate: DiscoveryCandidate, candidateIndex: number) => {
    try {
      if (candidate.type === 'event') {
        const startTime = candidate.startTime ? new Date(candidate.startTime) : new Date();
        const endTime = candidate.endTime ? new Date(candidate.endTime) : new Date(Date.now() + 3600000);
        const memberIds = candidate.memberIds?.length
          ? candidate.memberIds
          : currentUserId
            ? [currentUserId]
            : [];
        
        await createEvent({
          title: candidate.title,
          description: candidate.description,
          startTime,
          endTime,
          memberIds,
          location: candidate.location,
          isAllDay: candidate.isAllDay,
        });

        await confirmDiscovery(discovery.discoveryId, candidateIndex);
        showToast('Item created successfully', 'success');

        // Navigate to the event day
        navigate('/', { state: { view: 'day', date: startTime } });
      } else {
        await createTodo({
          title: candidate.title,
          description: candidate.description,
          dueDateTime: candidate.dueDateTime ? new Date(candidate.dueDateTime) : undefined,
          priority: candidate.priority,
          assigneeId: candidate.assigneeId,
        });

        await confirmDiscovery(discovery.discoveryId, candidateIndex);
        showToast('Item created successfully', 'success');

        // Navigate to todos for new todo
        navigate('/todos');
      }
    } catch (error: any) {
      console.error('Error confirming discovery:', error);
      showToast(error.message || 'Failed to create item', 'error');
    }
  };

  const handleDismissClick = (discoveryId: string) => {
    setDismissDiscoveryId(discoveryId);
  };

  const handleDismissConfirm = async () => {
    if (!dismissDiscoveryId) return;
    const idToDismiss = dismissDiscoveryId;
    setDismissDiscoveryId(null);
    try {
      await dismissDiscovery(idToDismiss);
      showToast('Discovery dismissed', 'success');
    } catch (error: any) {
      console.error('Error dismissing discovery:', error);
      showToast(error.message || 'Failed to dismiss discovery', 'error');
    }
  };

  const handleDismissCandidate = async (discoveryId: string, candidateIndex: number) => {
    try {
      await dismissCandidate(discoveryId, candidateIndex);
      showToast('Candidate dismissed', 'success');
    } catch (error: any) {
      console.error('Error dismissing candidate:', error);
      showToast(error.message || 'Failed to dismiss candidate', 'error');
    }
  };

  // Polling for processing discoveries - use ref to avoid stale closures
  const [pollingIds, setPollingIds] = useState<Set<string>>(new Set());
  const pollingIntervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  const startPolling = (discoveryId: string) => {
    if (pollingIntervalsRef.current.has(discoveryId)) return; // Already polling
    setPollingIds(prev => new Set(prev).add(discoveryId));
    
    const pollInterval = setInterval(async () => {
      try {
        await fetchDiscoveries('processing', false, { silent: true });
        await fetchDiscoveries('pending', false, { silent: true });
        
        // Get fresh data from store (avoid stale closure)
        const currentDiscoveries = useAIDiscoveryStore.getState().discoveries;
        const discovery = currentDiscoveries.find(d => d.discoveryId === discoveryId);
        if (discovery && discovery.status !== 'processing') {
          pollingIntervalsRef.current.delete(discoveryId);
          clearInterval(pollInterval);
          setPollingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(discoveryId);
            return newSet;
          });
          
          if (discovery.status === 'pending' && discovery.candidates.length > 0) {
            showToast(`Found ${discovery.candidates.length} candidate(s)`, 'success');
          } else if (discovery.status === 'failed') {
            showToast('Processing failed. Please try again.', 'error');
          }
        }
      } catch (error) {
        console.error('Error polling discovery:', error);
      }
    }, 2000); // Poll every 2 seconds
    pollingIntervalsRef.current.set(discoveryId, pollInterval);

    // Stop polling after 2 minutes
    setTimeout(() => {
      if (pollingIntervalsRef.current.has(discoveryId)) {
        pollingIntervalsRef.current.delete(discoveryId);
        clearInterval(pollInterval);
        setPollingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(discoveryId);
          return newSet;
        });
      }
    }, 120000);
  };

  useEffect(() => {
    // Auto-poll for any processing discoveries
    const processingDiscoveries = discoveries.filter(d => d.status === 'processing');
    processingDiscoveries.forEach(d => {
      if (!pollingIds.has(d.discoveryId)) {
        startPolling(d.discoveryId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveries.length]); // Re-check when discoveries change

  const pendingDiscoveries = discoveries.filter(d => d.status === 'pending');
  const processingDiscoveries = discoveries.filter(d => d.status === 'processing');
  const failedDiscoveries = discoveries.filter(d => d.status === 'failed');

  return (
    <div className="h-screen flex flex-col pr-20">
      <NavBar>
        <span className={`${tokens.text.primary} font-semibold`}>AI Discoveries</span>
      </NavBar>

      <div className="flex-1 overflow-y-auto p-2">
        {/* Upload Section */}
        <div className={`${tokens.card.bg} ${tokens.card.border} border rounded-landing p-4 mb-4 ${tokens.card.shadow}`}>
          <h2 className={`text-lg font-semibold ${tokens.text.primary} mb-4`}>Upload Audio or Image</h2>
          
          <div className="flex flex-wrap gap-4">
            {/* Audio Recording */}
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-sm font-medium ${tokens.text.secondary} mb-2`}>
                Record Audio (max 25s)
              </label>
              <div className="flex items-center gap-2">
                {!recording && !audioBlob && (
                  <button
                    type="button"
                    onClick={handleStartRecordingClick}
                    onPointerUp={handleStartRecordingClick}
                    className={`${tokens.button.primary} px-4 py-3 flex items-center justify-center gap-2 touch-manipulation`}
                  >
                    <Icon name="plus" size="sm" className={tokens.text.inverse} />
                    Start Recording
                  </button>
                )}
                {recording && (
                  <>
                    <button
                      onClick={stopRecording}
                      className={`${tokens.button.secondary} px-4 py-3 flex items-center justify-center gap-2 touch-manipulation`}
                    >
                      <Icon name="check" size="sm" className={tokens.text.primary} />
                      Stop ({25 - recordingTime}s)
                    </button>
                  </>
                )}
                {audioBlob && !recording && (
                  <div className="flex items-center gap-2">
                    <audio src={audioUrl || undefined} controls className="flex-1" />
                    <button
                      onClick={handleAudioUpload}
                      disabled={isUploading || isProcessing}
                      className={`${tokens.button.primary} px-4 py-3 flex items-center justify-center gap-2 touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isUploading || isProcessing ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <>
                          <Icon name="check" size="sm" className={tokens.text.inverse} />
                          Process
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setAudioBlob(null);
                        if (audioUrl) {
                          URL.revokeObjectURL(audioUrl);
                          setAudioUrl(null);
                        }
                      }}
                      className={`${tokens.button.secondary} min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation`}
                    >
                      <Icon name="close" size="sm" className={tokens.text.primary} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Audio File Upload */}
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-sm font-medium ${tokens.text.secondary} mb-2`}>
                Upload Audio File
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(file, 'audio');
                  }
                }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isProcessing}
                className={`${tokens.button.secondary} px-4 py-3 flex items-center justify-center gap-2 touch-manipulation disabled:opacity-70 disabled:cursor-not-allowed min-h-[48px]`}
              >
                {isUploading || isProcessing ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className={tokens.text.secondary}>{isUploading ? 'Uploading...' : 'Processing...'}</span>
                  </>
                ) : (
                  <>
                    <Icon name="plus" size="sm" className={tokens.text.primary} />
                    Choose Audio File
                  </>
                )}
              </button>
            </div>

            {/* Image Upload */}
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-sm font-medium ${tokens.text.secondary} mb-2`}>
                Upload Image
              </label>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileUpload(file, 'image');
                  }
                }}
              />
              <button
                onClick={() => imageInputRef.current?.click()}
                disabled={isUploading || isProcessing}
                className={`${tokens.button.secondary} px-4 py-3 flex items-center justify-center gap-2 touch-manipulation disabled:opacity-70 disabled:cursor-not-allowed min-h-[48px]`}
              >
                {isUploading || isProcessing ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className={tokens.text.secondary}>{isUploading ? 'Uploading...' : 'Processing...'}</span>
                  </>
                ) : (
                  <>
                    <Icon name="plus" size="sm" className={tokens.text.primary} />
                    Choose Image
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Discoveries List */}
        <div className={`${tokens.card.bg} ${tokens.card.border} border rounded-landing p-4 ${tokens.card.shadow}`}>
          <h2 className={`text-lg font-semibold ${tokens.text.primary} mb-4`}>
            Discoveries
            {processingDiscoveries.length > 0 && (
              <span className={`ml-2 text-sm font-normal ${tokens.text.muted}`}>
                ({processingDiscoveries.length} processing, {pendingDiscoveries.length} pending)
              </span>
            )}
            {processingDiscoveries.length === 0 && pendingDiscoveries.length > 0 && (
              <span className={`ml-2 text-sm font-normal ${tokens.text.muted}`}>
                ({pendingDiscoveries.length} pending)
              </span>
            )}
          </h2>

          {isLoading && discoveries.length === 0 ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : processingDiscoveries.length === 0 && pendingDiscoveries.length === 0 && failedDiscoveries.length === 0 ? (
            <div className={`text-center py-8 ${tokens.text.muted}`}>
              <Icon name="discoveries" size="xl" className="mx-auto mb-2 opacity-50" />
              <p>No discoveries. Upload audio or images to discover events and todos.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {/* Processing Discoveries */}
              {processingDiscoveries.map((discovery) => (
                <div key={discovery.discoveryId} className={`${tokens.bg.secondary} rounded-landing p-3 border ${tokens.border.light}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {discovery.sourceType === 'image' && (
                        <div className="flex-shrink-0">
                          {imageUrlCache[discovery.discoveryId] ? (
                            <button
                              type="button"
                              onClick={() => setFullImageUrl(imageUrlCache[discovery.discoveryId])}
                              className="block rounded-landing overflow-hidden border-2 border-transparent hover:border-[var(--accent)] transition-colors touch-manipulation active:scale-[0.98]"
                            >
                              <img
                                src={imageUrlCache[discovery.discoveryId]}
                                alt="Discovery"
                                className="w-20 h-20 object-cover opacity-90"
                              />
                            </button>
                          ) : (
                            <div className={`w-20 h-20 rounded-landing ${tokens.bg.panel} flex items-center justify-center`}>
                              <LoadingSpinner size="sm" />
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <LoadingSpinner size="sm" />
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Icon 
                              name={discovery.sourceType === 'audio' ? 'clock' : 'location'} 
                              size="sm" 
                              className={tokens.text.muted}
                            />
                            <span className={`text-sm font-medium ${tokens.text.secondary}`}>
                              {discovery.sourceType === 'audio' ? 'Audio' : 'Image'} • Processing...
                            </span>
                          </div>
                          <p className={`text-xs ${tokens.text.muted}`}>
                            Analyzing content, please wait...
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDismissClick(discovery.discoveryId)}
                      className={`${tokens.button.secondary} min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation`}
                    >
                      <Icon name="close" size="sm" className={tokens.text.primary} />
                    </button>
                  </div>
                </div>
              ))}

              {/* Failed Discoveries */}
              {failedDiscoveries.map((discovery) => (
                <div key={discovery.discoveryId} className={`${tokens.bg.secondary} rounded-landing p-3 border ${tokens.border.light} border-red-300 dark:border-red-700`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0 flex flex-row gap-3 items-start">
                      {discovery.sourceType === 'image' && (
                        <div className="flex-shrink-0">
                          {imageUrlCache[discovery.discoveryId] ? (
                            <button
                              type="button"
                              onClick={() => setFullImageUrl(imageUrlCache[discovery.discoveryId])}
                              className="block rounded-landing overflow-hidden border-2 border-transparent hover:border-[var(--accent)] transition-colors touch-manipulation active:scale-[0.98]"
                            >
                              <img
                                src={imageUrlCache[discovery.discoveryId]}
                                alt="Discovery"
                                className="w-20 h-20 object-cover opacity-75"
                              />
                            </button>
                          ) : (
                            <div className={`w-20 h-20 rounded-landing ${tokens.bg.panel} flex items-center justify-center`}>
                              <LoadingSpinner size="sm" />
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon name="warning" size="sm" className="text-red-500" />
                          <span className={`text-sm font-medium text-red-600 dark:text-red-400`}>
                            Processing Failed
                          </span>
                        </div>
                        <p className={`text-xs ${tokens.text.muted}`}>
                          {discovery.sourceType === 'audio' ? 'Audio' : 'Image'} processing failed. Please try again.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDismissClick(discovery.discoveryId)}
                      className={`${tokens.button.secondary} min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation`}
                    >
                      <Icon name="close" size="sm" className={tokens.text.primary} />
                    </button>
                  </div>
                </div>
              ))}

              {/* Pending Discoveries */}
              {pendingDiscoveries.map((discovery) => (
                <div key={discovery.discoveryId} className={`${tokens.bg.secondary} rounded-landing p-3 border ${tokens.border.light}`}>
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <div className="flex-1 min-w-0 flex flex-row gap-3 items-start">
                      {discovery.sourceType === 'image' && (
                        <div className="flex-shrink-0">
                          {imageUrlCache[discovery.discoveryId] ? (
                            <button
                              type="button"
                              onClick={() => setFullImageUrl(imageUrlCache[discovery.discoveryId])}
                              className="block rounded-landing overflow-hidden border-2 border-transparent hover:border-[var(--accent)] transition-colors touch-manipulation active:scale-[0.98]"
                            >
                              <img
                                src={imageUrlCache[discovery.discoveryId]}
                                alt="Discovery"
                                className="w-20 h-20 object-cover"
                              />
                            </button>
                          ) : (
                            <div className={`w-20 h-20 rounded-landing ${tokens.bg.panel} flex items-center justify-center`}>
                              <LoadingSpinner size="sm" />
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Icon 
                            name={discovery.sourceType === 'audio' ? 'clock' : 'location'} 
                            size="sm" 
                            className={`flex-shrink-0 ${tokens.text.muted}`}
                          />
                          <span className={`text-xs font-medium ${tokens.text.secondary} truncate`}>
                            {discovery.sourceType === 'audio' ? 'Audio' : 'Image'} • {new Date(discovery.createdAt).toLocaleString()}
                          </span>
                        </div>
                        {discovery.transcript && (
                          <p className={`text-xs ${tokens.text.muted} whitespace-pre-wrap break-words`}>
                            "{discovery.transcript}"
                          </p>
                        )}
                        {discovery.rawNotes && (
                          <p className={`text-xs ${tokens.text.muted} mt-1 italic whitespace-pre-wrap break-words`}>
                            {discovery.rawNotes}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDismissClick(discovery.discoveryId)}
                      className={`${tokens.button.secondary} min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation flex-shrink-0`}
                    >
                      <Icon name="close" size="sm" className={tokens.text.primary} />
                    </button>
                  </div>

                  <div className="space-y-2">
                    {discovery.candidates.map((candidate, idx) => (
                      <div 
                        key={idx} 
                        className={`${tokens.bg.primary} rounded-landing p-2 border ${tokens.border.light}`}
                      >
                        <div className="flex flex-row gap-2 items-start">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                              <span className={`text-xs font-semibold px-2 py-1 rounded ${tokens.bg.panel} ${tokens.text.primary}`}>
                                {candidate.type === 'event' ? 'Event' : 'Todo'}
                              </span>
                              {candidate.duplicateInfo?.status === 'probable' && (
                                <span className={`text-xs font-semibold px-2 py-1 rounded bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200`}>
                                  Possible Duplicate
                                </span>
                              )}
                              {(candidate.type === 'event' && candidate.startTime && new Date(candidate.startTime) < new Date()) && (
                                <span className="text-xs font-semibold px-2 py-1 rounded bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200">
                                  In the past
                                </span>
                              )}
                              {(candidate.type === 'todo' && candidate.dueDateTime && new Date(candidate.dueDateTime) < new Date()) && (
                                <span className="text-xs font-semibold px-2 py-1 rounded bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200">
                                  Past due
                                </span>
                              )}
                            </div>
                            <h3 className={`text-sm font-semibold ${tokens.text.primary} mb-0.5 line-clamp-1`}>
                              {candidate.title}
                            </h3>
                            {candidate.description && (
                              <p className={`text-xs ${tokens.text.secondary} mb-0.5 whitespace-pre-wrap break-words`}>
                                {candidate.description}
                              </p>
                            )}
                            {candidate.duplicateInfo?.reason && (
                              <p className={`text-xs ${tokens.text.muted} italic`}>
                                {candidate.duplicateInfo.reason}
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0 text-right space-y-0.5 text-xs">
                            {candidate.type === 'event' && candidate.startTime && (
                              <p className={`text-xs ${tokens.text.muted}`}>
                                {candidate.isAllDay 
                                  ? formatDate(new Date(candidate.startTime), 'short')
                                  : formatTimeRange(new Date(candidate.startTime), new Date(candidate.endTime || candidate.startTime), { showDate: true })
                                }
                              </p>
                            )}
                            {candidate.type === 'event' && candidate.location && (
                              <p className={`text-xs ${tokens.text.muted} flex items-center justify-end gap-1`}>
                                <Icon name="location" size="sm" className="flex-shrink-0" />
                                {candidate.location}
                              </p>
                            )}
                            {candidate.type === 'event' && candidate.memberIds && candidate.memberIds.length > 0 && family?.members && (() => {
                              const names = candidate.memberIds!.map(id => family!.members.find(m => m.userId === id)?.name).filter(Boolean);
                              return names.length > 0 ? <p className={`text-xs ${tokens.text.muted}`}>With: {names.join(', ')}</p> : null;
                            })()}
                            {candidate.type === 'todo' && candidate.dueDateTime && (
                              <p className={`text-xs ${tokens.text.muted}`}>
                                Due: {formatDateTime(new Date(candidate.dueDateTime))}
                              </p>
                            )}
                            {candidate.type === 'todo' && candidate.priority && candidate.priority !== 'medium' && (
                              <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${
                                candidate.priority === 'critical' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                                candidate.priority === 'high' ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' :
                                candidate.priority === 'low' ? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300' :
                                'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                              }`}>
                                {candidate.priority.charAt(0).toUpperCase() + candidate.priority.slice(1)}
                              </span>
                            )}
                            {candidate.type === 'todo' && candidate.assigneeId && family?.members && (
                              <p className={`text-xs ${tokens.text.muted}`}>
                                {family.members.find(m => m.userId === candidate.assigneeId)?.name}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleConfirm(discovery, candidate, idx)}
                            disabled={!family}
                            className={`${tokens.button.primary} px-3 py-2 text-sm flex items-center justify-center gap-1.5 touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]`}
                          >
                            <Icon name="check" size="sm" className={tokens.text.inverse} />
                            Confirm
                          </button>
                          <button
                            onClick={() => handleDismissCandidate(discovery.discoveryId, idx)}
                            className={`${tokens.button.secondary} px-3 py-2 text-sm flex items-center justify-center gap-1.5 touch-manipulation min-h-[44px]`}
                          >
                            <Icon name="close" size="sm" className={tokens.text.primary} />
                            Dismiss
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CoreNavRail active="discoveries" />

      {/* Dismiss discovery confirmation */}
      <ConfirmDialog
        isOpen={dismissDiscoveryId !== null}
        title="Dismiss Discovery"
        message="Are you sure you want to dismiss this discovery? You won't be able to add these items to your calendar."
        confirmText="Dismiss"
        cancelText="Cancel"
        onConfirm={handleDismissConfirm}
        onCancel={() => setDismissDiscoveryId(null)}
        isDestructive={true}
      />

      {/* Full image modal */}
      {fullImageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setFullImageUrl(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setFullImageUrl(null)}
          aria-label="Close image"
        >
          <img
            src={fullImageUrl}
            alt="Discovery full size"
            className="max-w-full max-h-full object-contain rounded-landing"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
